import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui';
import { Shield, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores';

export function TwoFactorVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens, setUser } = useAuthStore();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('tempToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setTempToken(token);
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tempToken || code.length < 6) {
      toast.error('请输入完整的验证码');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/login/2fa', {
        tempToken,
        code,
      });

      if (response.data.success) {
        const { user, access_token, refresh_token, expires_in } = response.data.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        setTokens({ access_token, refresh_token, expires_in: expires_in || 3600 });
        setUser(user);
        toast.success('登录成功');
        navigate('/');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '验证失败');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseBackupCode = () => {
    // Allow longer input for backup codes
    navigate(`/2fa/backup?tempToken=${tempToken}`);
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">双因素认证</CardTitle>
          <CardDescription>
            请输入验证器应用中的6位验证码
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-3xl tracking-widest font-mono"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? '验证中...' : '验证'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-muted-foreground mb-2">
              无法访问验证器？
            </p>
            <Button variant="link" onClick={handleUseBackupCode}>
              使用备用码
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回登录
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
